// 批次 23（#25，Gitea issue #25）：form 模組查詢頁群 complexBody×2。
//
//   - AllFormListBody＝綜合表單查詢（form-all-form-list，origin form/allFormList.jsp 460 行）：
//     查詢表單 13 欄＋doQuery 驗證鏉（:248-267：學年度＋醫院＋科部＋表單發送日 →
//     '請選擇'+逗號清單，showAlertDialog）＋queryFormListAjax（:270）＋結果表 9 欄（:76-133
//     欄定義）＋列點擊（:134-146：purpose=='studyItem'→showStudyRecordFill window.open
//     studyRecordFormN；否則 showFillForm＝formList.js:113-122 window.open fillForm 帶參）。
//   - FormStaticListBody＝表單統計（form-form-static-list，origin form/formStaticList.jsp 315 行）：
//     查詢表單 11 欄＋doQuery 驗證鏉（:140-158：學年度＋醫院＋表單發送日；**無科部檢查**）＋
//     queryFormStaticListAjax（:163）＋統計表 7 欄（:34-90；無列互動）。
//
// 誠實標註（KNOWN-NOT-DONE D23-x）：
//   - 學年度必填檢查在 legacy 有條件（<c:if test="${empty defaultPpurpose and empty defaultJobtype}">
//     深鏈帶預設用途/職類時跳過；allFormList.jsp:250-252）——雛形固定必填（一般進場＝條件成立）。
//   - 醫院/科部/學年度預選：legacy 由 server＋initSearchForm JS 級聯預選帳號所屬值
//     （allFormList.jsp:44-75；raw-dom annualId 預選 115）——雛形以池首值/年度 115 預選【雛形值待實機校準】。
//   - 查詢為伺服器端過濾（queryFormListAjax）——雛形 filters 直送 MockDataSource（C5-h；
//     matchesFilters 對未知鍵＝全等比對，種子列不帶查詢鍵＝不被過濾），過濾語意【雛形值待實機校準】。
//   - 整列點擊開表單 → 名稱連結點擊等價（legacy formName 本身渲染 <a>；課程查詢 showEdit 同款先例）。
//   - window.open（showFillForm/showStudyRecordFill）以站內導航鏡像（B19-2/D19-2 先例；
//     query 帶參照 URL 形狀，目標頁為 0 契約參數頁（B4-2 通用模板）不消費參數＝D20-7 同款）。
//   - 送出日期 epdateutil.formatDate → 種子直接存 YYYY-MM-DD。
//   - resp.desc 成功/失敗文案為 server 回應【雛形值待實機校準】→ 查詢失敗走 legacyAlertFallback。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { FormWrap, toUiKey } from '@/pages/templates/shared';
import { useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import type { Row } from '@/mocks/types';
import {
  ACCOUNT_DEPARTMENT_OPTIONS,
  ACCOUNT_JOB_TYPE_OPTIONS,
  ACCOUNT_ORGANIZATION_OPTIONS,
  COURSE_CLAZZ_PLAN_TARGET_OPTIONS,
  TRAINING_STUDENT_OPTIONS,
} from '@/mocks/resource-rules';
import { legacyAlertFallback } from '../templates/page-behaviors';
import { useNavigate } from 'react-router-dom';

/** 學年度選項（allFormList.jsp:342-348：不限制＋年度清單；raw-dom 預選 115）。 */
const FORM_ANNUAL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '114', label: '114' },
  { value: '115', label: '115' },
];

/** 表單用途選項（allFormList.jsp:369-378：全部/計畫表單/課程表單；studyItem 在 JSP 註解掉）。 */
const FORM_PURPOSE_QUERY_OPTIONS: readonly { value: string; label: string }[] = [
  { value: '', label: '全部' },
  { value: 'plan', label: '計畫表單' },
  { value: 'class', label: '課程表單' },
];

// ---------------------------------------------------------------------------
// 共用：查詢欄（DispatchExamBody queryField 同款）
// ---------------------------------------------------------------------------

function QueryField({
  pageId,
  groupKey,
  keyName,
  label,
  required,
  control,
}: {
  pageId: string;
  groupKey: string;
  keyName: string;
  label: string;
  required: boolean;
  control: ReactElement;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* 批次 50（工作項 4）：htmlFor 字尾 toUiKey kebab 化，與呼叫端 kebab 字面 uiId 成對
          （keyName 本身是 name/payload key 域，參數值不動；groupKey 值全小寫不需轉）。 */}
      <label className="w-28 shrink-0 text-sm" htmlFor={`${pageId}-${groupKey}-query-${toUiKey(keyName)}`}>
        {required ? <span className="text-danger">*</span> : null}
        {label}
      </label>
      {control}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 綜合表單查詢（form-all-form-list）
// ---------------------------------------------------------------------------

/** 狀態欄（allFormList.jsp:119-127：isDone==1→完成／進行中）。 */
function allFormStatusOf(row: Row): string {
  return String(row['isDone'] ?? '') === '1' ? '完成' : '進行中';
}

export function AllFormListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const navigate = useNavigate();

  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數
  // （規格 §7.1；照已接線 7 檔同構一行式；override 命中含空陣列即移除 placeholder 空選項）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // 查詢表單值（raw-dom 預選：年度 115；醫院/科部由 server+JS 預選帳號值→池首【雛形值待實機校準】）。
  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobtypeId, setJobtypeId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [planTargetId, setPlanTargetId] = useState('');
  const [departmentId, setDepartmentId] = useState(ACCOUNT_DEPARTMENT_OPTIONS[0]!.value);
  const [formName, setFormName] = useState('');
  const [queryStartDateS, setQueryStartDateS] = useState('');
  const [queryStartDateE, setQueryStartDateE] = useState('');
  const [searchStudentCode, setSearchStudentCode] = useState('');
  const [studentId, setStudentId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    // 驗證鏉（allFormList.jsp:248-276）：'請選擇'+逗號清單。
    const missing: string[] = [];
    // 學年度檢查有 c:if 條件（深鏈帶 defaultPpurpose/defaultJobtype 時跳過）——雛形固定必填（D23-1）。
    if (annualId === '') {
      missing.push('學年度');
    }
    if (organizationId === '' || Number.parseInt(organizationId, 10) < 0) {
      missing.push('醫院');
    }
    if (departmentId === '' || Number.parseInt(departmentId, 10) < 0) {
      missing.push('科部');
    }
    if (queryStartDateS === '' || queryStartDateE === '') {
      missing.push('表單發送日');
    }
    if (missing.length > 0) {
      await dialogs.alert(`請選擇${missing.join(',')}`);
      return;
    }
    setBusy(true);
    try {
      // form/queryFormListAjax（allFormList.jsp:282；server 端過濾）→ filters 直送 MockDataSource。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: {
          annualId,
          organizationId,
          jobtypeId,
          purpose,
          planTargetId,
          departmentId,
          formName,
          queryStartDateS,
          queryStartDateE,
          searchStudentCode,
          studentId,
          keyword,
          deptqry: '',
        },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  /** 列點擊（allFormList.jsp:134-146）：studyItem→showStudyRecordFill；其餘 showFillForm。 */
  function openForm(row: Row): void {
    const purposeValue = String(row['purpose'] ?? '');
    if (purposeValue === 'studyItem') {
      // showStudyRecordFill（allFormList.jsp:311-322）：window.open studyRecordFormN 帶參。
      const params = new URLSearchParams({
        isWindowOpen: 'true',
        refTable: 'StudyRecordForm',
        refId: String(row['formId'] ?? ''),
        formKey: String(row['formKey'] ?? ''),
        phaseTargetId: String(row['phaseTargetId'] ?? ''),
        studyRecordFormId: String(row['formId'] ?? ''),
      });
      navigate(`/training/studyRecord/showStudyRecordFormN?${params.toString()}`);
      return;
    }
    // showFillForm（formList.js:113-122）：window.open fillForm 帶參（D19-2 站內導航鏡像）。
    const params = new URLSearchParams({
      isWindowOpen: 'true',
      formKey: String(row['formKey'] ?? ''),
      fromPage: 'classForm',
      refTable: String(row['fttRefTable'] ?? ''),
      refId: String(row['fttRefId'] ?? ''),
    });
    navigate(`/form/prompt/fillForm?${params.toString()}`);
  }

  const columns: readonly TableColumn[] = [
    { key: 'formName', header: '表單名稱', align: 'center' },
    { key: 'purposeName', header: '表單用途', align: 'center' },
    { key: 'planName', header: '訓練計畫/課程類別', align: 'center' },
    { key: 'phaseName', header: '階段/課程', align: 'center' },
    { key: 'classDate', header: '上課日期', align: 'center' },
    { key: 'departmentName', header: '部門', align: 'center' },
    { key: 'studentName', header: '學員', align: 'center' },
    { key: 'submitDate', header: '送出日期', align: 'center' },
    { key: 'isDone', header: '狀態', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      {/* hidden deptqry（allFormList.jsp:333；URL 參數帶入用，雛形值空）。 */}
      <input type="hidden" name="deptqry" value="" readOnly />
      <ContentPanel uiId={`${pageId}-panel`} title="綜合表單查詢" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（allFormList.jsp:330-444）。 */}
        {/* 批次 37（GNL-005-b Phase 2）：查詢欄位＋查詢鈕包 form 鏡像 legacy。 */}
        <FormWrap pageId={pageId}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'annualId',
            label: '學年度：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-form-query-annual-id`}
                name="annualId"
                value={annualId}
                onChange={setAnnualId}
                options={overrideOptions('annualId') ?? FORM_ANNUAL_OPTIONS}
                {...(overrideOptions('annualId') ? {} : { placeholder: '不限制' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'organizationId',
            label: '醫院：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-form-query-organization-id`}
                name="organizationId"
                value={organizationId}
                onChange={setOrganizationId}
                options={overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
                {...(overrideOptions('organizationId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'jobtypeId',
            label: '職類：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-form-query-jobtype-id`}
                name="jobtypeId"
                value={jobtypeId}
                onChange={setJobtypeId}
                options={overrideOptions('jobtypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS}
                {...(overrideOptions('jobtypeId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'purpose',
            label: '表單用途：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-form-query-purpose`}
                name="purpose"
                value={purpose}
                onChange={setPurpose}
                options={FORM_PURPOSE_QUERY_OPTIONS}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'planTargetId',
            label: '訓練計畫：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-form-query-plan-target-id`}
                name="planTargetId"
                value={planTargetId}
                onChange={setPlanTargetId}
                options={overrideOptions('planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS}
                {...(overrideOptions('planTargetId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'departmentId',
            label: '科部：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-form-query-department-id`}
                name="departmentId"
                value={departmentId}
                onChange={setDepartmentId}
                options={overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
                {...(overrideOptions('departmentId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'formName',
            label: '表單名稱：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-form-query-form-name`}
                name="formName"
                value={formName}
                onChange={setFormName}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'queryStartDateS',
            label: '表單發送日起：',
            required: true,
            control: (
              <TextInput
                uiId={`${pageId}-form-query-query-start-date-s`}
                name="queryStartDateS"
                value={queryStartDateS}
                onChange={setQueryStartDateS}
                maxLength={10}
                placeholder="YYYY-MM-DD"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'queryStartDateE',
            label: '表單發送日迄：',
            required: true,
            control: (
              <TextInput
                uiId={`${pageId}-form-query-query-start-date-e`}
                name="queryStartDateE"
                value={queryStartDateE}
                onChange={setQueryStartDateE}
                maxLength={10}
                placeholder="YYYY-MM-DD"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'searchStudentCode',
            label: '學員：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-form-query-search-student-code`}
                name="searchStudentCode"
                value={searchStudentCode}
                onChange={setSearchStudentCode}
                placeholder="(請輸入學員姓名或學員編號)"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'studentId',
            label: '學員：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-form-query-student-id`}
                name="studentId"
                value={studentId}
                onChange={setStudentId}
                options={overrideOptions('studentId') ?? TRAINING_STUDENT_OPTIONS}
                {...(overrideOptions('studentId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'form',
            keyName: 'keyword',
            label: '關鍵字：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-form-query-keyword`}
                name="keyword"
                value={keyword}
                onChange={setKeyword}
              />
            ),
          })}
          <div className="col-span-2 flex gap-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        </FormWrap>
        {/* 結果表（欄定義 allFormList.jsp:76-133；9 欄）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => `form-row-${String(row['formKey'] ?? '')}`}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            if (columnKey === 'formName') {
              return (
                <button
                  type="button"
                  className="cursor-pointer text-[#1ab394] underline"
                  data-ui-id={`${pageId}-result-row-${String(row['formKey'] ?? '')}-open`}
                  onClick={() => {
                    openForm(row);
                  }}
                >
                  {String(row['formName'] ?? '')}
                </button>
              );
            }
            if (columnKey === 'isDone') {
              return allFormStatusOf(row);
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 表單統計（form-form-static-list）
// ---------------------------------------------------------------------------

export function FormStaticListBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  const [annualId, setAnnualId] = useState('115');
  const [organizationId, setOrganizationId] = useState(ACCOUNT_ORGANIZATION_OPTIONS[0]!.value);
  const [jobtypeId, setJobtypeId] = useState('');
  const [purpose, setPurpose] = useState('');
  const [planTargetId, setPlanTargetId] = useState('');
  const [departmentId, setDepartmentId] = useState(ACCOUNT_DEPARTMENT_OPTIONS[0]!.value);
  const [formName, setFormName] = useState('');
  const [queryStartDateS, setQueryStartDateS] = useState('');
  const [queryStartDateE, setQueryStartDateE] = useState('');
  const [searchStudentCode, setSearchStudentCode] = useState('');
  const [studentId, setStudentId] = useState('');
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [busy, setBusy] = useState(false);

  async function doQuery(): Promise<void> {
    // 驗證鏉（formStaticList.jsp:140-158）：學年度＋醫院＋表單發送日（**無科部檢查**）。
    const missing: string[] = [];
    if (annualId === '') {
      missing.push('學年度');
    }
    if (organizationId === '' || Number.parseInt(organizationId, 10) < 0) {
      missing.push('醫院');
    }
    if (queryStartDateS === '' || queryStartDateE === '') {
      missing.push('表單發送日');
    }
    if (missing.length > 0) {
      await dialogs.alert(`請選擇${missing.join(',')}`);
      return;
    }
    setBusy(true);
    try {
      // form/queryFormStaticListAjax（formStaticList.jsp:163；server 端過濾）。
      const result = await dataSource.list(pageId, {
        page: 1,
        pageSize: 50,
        filters: {
          annualId,
          organizationId,
          jobtypeId,
          purpose,
          planTargetId,
          departmentId,
          formName,
          queryStartDateS,
          queryStartDateE,
          searchStudentCode,
          studentId,
        },
      });
      setRows(result.rows);
    } catch (cause: unknown) {
      await dialogs.alert(legacyAlertFallback(cause));
    } finally {
      setBusy(false);
    }
  }

  const columns: readonly TableColumn[] = [
    { key: 'startYM', header: '表單月份', align: 'center' },
    { key: 'purposeName', header: '表單用途', align: 'center' },
    { key: 'courseTypeName', header: '課程類別', align: 'center' },
    { key: 'formName', header: '表單名稱', align: 'center' },
    { key: 'departmentName', header: '部門', align: 'center' },
    { key: 'formNum', header: '表單數', align: 'center' },
    { key: 'formFinishNum', header: '完成表單數', align: 'center' },
  ];

  return (
    <div data-ui-id={pageId}>
      <ContentPanel uiId={`${pageId}-panel`} title="表單統計" headingLevel={panelHeadingLevelFor(pageId)}>
        {/* 查詢表單（formStaticList.jsp:175-315；欄位群與綜合查詢同構、無關鍵字/deptqry）。 */}
        <FormWrap pageId={pageId}>
        <div data-ui-id={`${pageId}-query`} className="mb-4 grid grid-cols-2 gap-2">
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'annualId',
            label: '學年度：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-static-query-annual-id`}
                name="annualId"
                value={annualId}
                onChange={setAnnualId}
                options={overrideOptions('annualId') ?? FORM_ANNUAL_OPTIONS}
                {...(overrideOptions('annualId') ? {} : { placeholder: '不限制' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'organizationId',
            label: '醫院：',
            required: true,
            control: (
              <Select
                uiId={`${pageId}-static-query-organization-id`}
                name="organizationId"
                value={organizationId}
                onChange={setOrganizationId}
                options={overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
                {...(overrideOptions('organizationId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'jobtypeId',
            label: '職類：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-static-query-jobtype-id`}
                name="jobtypeId"
                value={jobtypeId}
                onChange={setJobtypeId}
                options={overrideOptions('jobtypeId') ?? ACCOUNT_JOB_TYPE_OPTIONS}
                {...(overrideOptions('jobtypeId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'purpose',
            label: '表單用途：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-static-query-purpose`}
                name="purpose"
                value={purpose}
                onChange={setPurpose}
                options={FORM_PURPOSE_QUERY_OPTIONS}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'planTargetId',
            label: '訓練計畫：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-static-query-plan-target-id`}
                name="planTargetId"
                value={planTargetId}
                onChange={setPlanTargetId}
                options={overrideOptions('planTargetId') ?? COURSE_CLAZZ_PLAN_TARGET_OPTIONS}
                {...(overrideOptions('planTargetId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'departmentId',
            label: '科部：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-static-query-department-id`}
                name="departmentId"
                value={departmentId}
                onChange={setDepartmentId}
                options={overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
                {...(overrideOptions('departmentId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'formName',
            label: '表單名稱：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-static-query-form-name`}
                name="formName"
                value={formName}
                onChange={setFormName}
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'queryStartDateS',
            label: '表單發送日起：',
            required: true,
            control: (
              <TextInput
                uiId={`${pageId}-static-query-query-start-date-s`}
                name="queryStartDateS"
                value={queryStartDateS}
                onChange={setQueryStartDateS}
                maxLength={10}
                placeholder="YYYY-MM-DD"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'queryStartDateE',
            label: '表單發送日迄：',
            required: true,
            control: (
              <TextInput
                uiId={`${pageId}-static-query-query-start-date-e`}
                name="queryStartDateE"
                value={queryStartDateE}
                onChange={setQueryStartDateE}
                maxLength={10}
                placeholder="YYYY-MM-DD"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'searchStudentCode',
            label: '學員：',
            required: false,
            control: (
              <TextInput
                uiId={`${pageId}-static-query-search-student-code`}
                name="searchStudentCode"
                value={searchStudentCode}
                onChange={setSearchStudentCode}
                placeholder="(請輸入學員姓名或學員編號)"
              />
            ),
          })}
          {QueryField({
            pageId,
            groupKey: 'static',
            keyName: 'studentId',
            label: '學員：',
            required: false,
            control: (
              <Select
                uiId={`${pageId}-static-query-student-id`}
                name="studentId"
                value={studentId}
                onChange={setStudentId}
                options={overrideOptions('studentId') ?? TRAINING_STUDENT_OPTIONS}
                {...(overrideOptions('studentId') ? {} : { placeholder: '請選擇' })}
              />
            ),
          })}
          <div className="col-span-2 flex gap-2">
            <Button uiId={`${pageId}-query-btn`} variant="primary" size="sm" type="button" disabled={busy} onClick={() => void doQuery()}>
              查詢
            </Button>
          </div>
        </div>
        </FormWrap>
        {/* 統計表（欄定義 formStaticList.jsp:34-90；7 欄；無列互動）。 */}
        <Table
          uiId={`${pageId}-result-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => `static-row-${String(row['startYM'] ?? '')}-${String(row['formName'] ?? '')}`}
          emptyText={t('table.empty')}
          renderCell={(row, columnKey) => {
            // formNum/formFinishNum（:67-76/:77-88）：undefined→'0'。
            if (columnKey === 'formNum' || columnKey === 'formFinishNum') {
              const value = row[columnKey];
              return value === undefined || value === null ? '0' : String(value);
            }
            return String(row[columnKey] ?? '');
          }}
        />
      </ContentPanel>
      <ProcessingOverlay uiId={`${pageId}-processing`} open={busy} />
    </div>
  );
}
